import React, { useEffect, useState } from 'react';
import BottomSheet from '../components/BottomSheet';
import StarRating from '../components/StarRating';
import { storage } from '../../utils/platform';
import { openExternalUrl, urlToWeb } from '../utils/external';
import { canShowPurchaseCta } from '../utils/storeCompliance';
import { API_BASE } from '../../config/api';

// Mobile FR uniquement (06/07 ou +336/+337) — cohérent avec PHONE_RE backend.
const PHONE_RE = /^(?:\+33|0)[6-7]\d{8}$/;

function normalizePhone(input) {
  return String(input || '').replace(/[\s.-]/g, '');
}

export default function CollectReviewModal({ open, onClose, onSend }) {
  const [prenom, setPrenom] = useState('');
  const [telephone, setTelephone] = useState('');
  const [rating, setRating] = useState(5);
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState(null);
  const [errors, setErrors] = useState({});
  const [quota, setQuota] = useState(null); // { remaining, plan, unlimited, used, quota }

  const reset = () => {
    setPrenom(''); setTelephone(''); setRating(5);
    setErrors({}); setToast(null);
  };

  const close = () => {
    if (sending) return;
    reset();
    onClose && onClose();
  };

  // Charge le quota SMS du mois courant à l'ouverture de la modale.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await storage.get('token');
        if (!token) return;
        const res = await fetch(`${API_BASE}/api/dashboard/sms-usage`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (!cancelled) setQuota(data);
      } catch (e) {
        console.warn('[CollectReviewModal] sms-usage failed:', e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const validate = () => {
    const e = {};
    const p = prenom.trim();
    if (p.length < 1 || p.length > 50) e.prenom = 'Prénom requis (1-50 caractères)';
    const cleaned = normalizePhone(telephone);
    if (!cleaned) e.telephone = 'Téléphone requis';
    else if (!PHONE_RE.test(cleaned)) e.telephone = 'Mobile FR uniquement (ex : 06 12 34 56 78)';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSending(true);
    setToast(null);
    try {
      const data = await onSend({
        prenom: prenom.trim(),
        telephone: normalizePhone(telephone),
        rating, // conservé pour future analytics — backend l'ignore en V1
      });
      setToast(`📩 SMS envoyé à ${prenom.trim()}`);
      // Mise à jour du quota local depuis la réponse backend
      if (data && typeof data.remaining_quota !== 'undefined') {
        setQuota((prev) => ({
          ...(prev || {}),
          remaining: data.remaining_quota,
          plan: data.plan,
          unlimited: !!data.unlimited,
        }));
      }
      setTimeout(() => { reset(); onClose && onClose(); }, 1500);
    } catch (err) {
      if (err.code === 'quota_exceeded') {
        setToast('⚠️ Quota SMS dépassé ce mois');
      } else if (err.code === 'invalid_telephone') {
        setErrors((prev) => ({ ...prev, telephone: err.message }));
      } else if (err.code === 'invalid_prenom') {
        setErrors((prev) => ({ ...prev, prenom: err.message }));
      } else {
        setToast('❌ ' + (err.message || 'Erreur d\'envoi'));
      }
    } finally {
      setSending(false);
    }
  };

  // Widget quota
  let quotaLine = null;
  if (quota) {
    if (quota.unlimited) {
      quotaLine = <div style={styles.quotaLine}>SMS illimités sur votre plan {quota.plan || ''}</div>;
    } else if (typeof quota.remaining === 'number') {
      const r = quota.remaining;
      if (r === 0) {
        // Chantier Découverte (30/07/2026) : on distingue « quota épuisé » (le
        // plan inclut des SMS, ils sont consommés) de « non inclus » (quota 0 dès
        // le départ — plan Découverte). Le second message serait trompeur sous
        // l'étiquette « Quota atteint ».
        const nonInclus = quota.quota === 0;
        quotaLine = (
          <div style={{ ...styles.quotaLine, color: '#C8442A' }}>
            {/* ⚠️ App Store 3.1.3(f) : sur iOS, ni nom de formule, ni volume
                vendu, ni lien — ce serait un appel à l'achat hors de l'app. */}
            {canShowPurchaseCta()
              ? (nonInclus
                  ? 'Les relances SMS ne sont pas incluses dans votre formule. Passez à Pro pour 500 SMS/mois.'
                  : 'Quota atteint — passez au plan Pro pour 500 SMS/mois.')
              : (nonInclus
                  ? 'Les relances SMS ne sont pas incluses dans votre formule actuelle.'
                  : 'Quota de SMS atteint pour ce mois.')}{' '}
            {canShowPurchaseCta() && (
              <button
                type="button"
                onClick={() => openExternalUrl(urlToWeb('/tarifs'))}
                style={styles.quotaLink}
              >
                Voir les plans →
              </button>
            )}
          </div>
        );
      } else {
        quotaLine = (
          <div style={styles.quotaLine}>
            Il vous reste <strong>{r}</strong> SMS ce mois{quota.plan ? ` · plan ${quota.plan}` : ''}
          </div>
        );
      }
    }
  }

  const quotaBlocked = quota && !quota.unlimited && quota.remaining === 0;

  return (
    <BottomSheet open={open} onClose={close} title="Collecter un avis" height="auto">
      {quotaLine}

      <div style={styles.field}>
        <label style={styles.label}>Prénom du client</label>
        <input
          type="text"
          value={prenom}
          onChange={(e) => setPrenom(e.target.value)}
          placeholder="Marie"
          style={{ ...styles.input, ...(errors.prenom ? styles.inputError : null) }}
          disabled={sending}
        />
        {errors.prenom && <span style={styles.errorMsg}>{errors.prenom}</span>}
      </div>

      <div style={styles.field}>
        <label style={styles.label}>Téléphone mobile</label>
        <input
          type="tel"
          inputMode="tel"
          value={telephone}
          onChange={(e) => setTelephone(e.target.value)}
          placeholder="06 12 34 56 78"
          style={{ ...styles.input, ...(errors.telephone ? styles.inputError : null) }}
          disabled={sending}
        />
        {errors.telephone && <span style={styles.errorMsg}>{errors.telephone}</span>}
      </div>

      <div style={styles.field}>
        <label style={styles.label}>Note ressentie (optionnel)</label>
        <div style={{ paddingTop: 4 }}>
          <StarRating rating={rating} size={28} onChange={setRating} />
        </div>
      </div>

      <button
        type="button"
        onClick={handleSubmit}
        disabled={sending || quotaBlocked}
        style={{ ...styles.cta, opacity: (sending || quotaBlocked) ? 0.7 : 1 }}
      >
        {sending ? (
          <span style={styles.ctaLoading}>
            <span style={styles.spinner} aria-hidden /> Envoi…
          </span>
        ) : 'Envoyer le SMS'}
      </button>

      {toast && <div style={styles.toast}>{toast}</div>}

      <style>{`
        @keyframes revyoSpinSm2 { from { transform: rotate(0); } to { transform: rotate(360deg); } }
      `}</style>
    </BottomSheet>
  );
}

const styles = {
  quotaLine: {
    fontSize: 12,
    color: '#6B6B6B',
    background: '#FFF5F0',
    padding: '8px 12px',
    borderRadius: 10,
    marginBottom: 14,
    lineHeight: 1.4,
  },
  quotaLink: {
    background: 'transparent',
    border: 'none',
    color: '#E8502A',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 12,
    padding: 0,
    marginLeft: 4,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    marginBottom: 14,
  },
  label: {
    fontSize: 12,
    fontWeight: 600,
    color: '#6B6B6B',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  input: {
    border: '1px solid #E5E5E0',
    background: '#FFFFFF',
    borderRadius: 10,
    padding: '12px 14px',
    fontSize: 15,
    fontFamily: 'inherit',
    color: '#1A1A1A',
    outline: 'none',
  },
  inputError: {
    borderColor: '#C8442A',
  },
  errorMsg: {
    color: '#C8442A',
    fontSize: 12,
    marginTop: 2,
  },
  cta: {
    width: '100%',
    background: '#E8502A',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 12,
    padding: '14px 16px',
    fontSize: 15,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'inherit',
    marginTop: 8,
  },
  ctaLoading: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    justifyContent: 'center',
  },
  spinner: {
    width: 14,
    height: 14,
    border: '2px solid #FFFFFF',
    borderTopColor: 'transparent',
    borderRadius: '50%',
    animation: 'revyoSpinSm2 0.8s linear infinite',
    display: 'inline-block',
  },
  toast: {
    marginTop: 12,
    background: '#E8F5EE',
    color: '#2D7A4A',
    fontSize: 13,
    fontWeight: 600,
    padding: '10px 14px',
    borderRadius: 10,
    textAlign: 'center',
  },
};
